/**
 * API Client Service
 * Handles all HTTP requests to the FastAPI backend
 */

import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosError,
  AxiosResponse,
} from "axios";
import * as Types from "@/types";

// Configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:13000/api/v1";
const API_TIMEOUT = 30000; // 30 seconds

export class APIError extends Error {
  constructor(
    public status: number,
    public detail: string,
    message?: string
  ) {
    super(message || detail);
    this.name = "APIError";
  }
}

/**
 * API Client for all backend requests
 */
class APIClient {
  private client: AxiosInstance;
  private token: string | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: API_TIMEOUT,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError<Types.ErrorResponse>) => {
        if (error.response?.status === 401) {
          // Clear token and redirect to login
          this.clearToken();
          window.location.href = "/login";
        }

        const detail =
          error.response?.data?.detail || error.message || "Unknown error";
        throw new APIError(
          error.response?.status || 500,
          detail as string,
          `API Error: ${detail}`
        );
      }
    );
  }

  /**
   * Set authentication token
   */
  setToken(token: string) {
    this.token = token;
    this.client.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    localStorage.setItem("auth_token", token);
  }

  /**
   * Get current token
   */
  getToken(): string | null {
    return this.token;
  }

  /**
   * Clear authentication token
   */
  clearToken() {
    this.token = null;
    delete this.client.defaults.headers.common["Authorization"];
    localStorage.removeItem("auth_token");
  }

  /**
   * Load token from storage (on app init)
   */
  loadTokenFromStorage() {
    const token = localStorage.getItem("auth_token");
    if (token) {
      this.setToken(token);
    }
  }

  // =========================================================================
  // Authentication
  // =========================================================================

  async login(request: Types.LoginRequest): Promise<Types.AuthToken> {
    const response = await this.client.post<Types.AuthToken>(
      "/auth/login",
      request
    );
    if (response.data.access_token) {
      this.setToken(response.data.access_token);
    }
    return response.data;
  }

  async logout(): Promise<void> {
    try {
      await this.client.post("/auth/logout");
    } finally {
      this.clearToken();
    }
  }

  async getCurrentUser(): Promise<Types.User> {
    const response = await this.client.get<Types.User>("/auth/me");
    return response.data;
  }

  // =========================================================================
  // Material Requests (Phase 2)
  // =========================================================================

  async createMaterialRequest(
    request: Types.MaterialRequestCreateRequest
  ): Promise<Types.MaterialRequest> {
    const response = await this.client.post<Types.MaterialRequest>(
      "/material-requests/request",
      request
    );
    return response.data;
  }

  async listMaterialRequests(
    params?: Partial<Types.ListQuery> & { operation_id?: string; status?: string }
  ): Promise<Types.PagedResponse<Types.MaterialRequest>> {
    const response = await this.client.get<
      Types.PagedResponse<Types.MaterialRequest>
    >("/material-requests/request", { params });
    return response.data;
  }

  async getMaterialRequest(id: string): Promise<Types.MaterialRequest> {
    const response = await this.client.get<Types.MaterialRequest>(
      `/material-requests/request/${id}`
    );
    return response.data;
  }

  async updateMaterialRequest(
    id: string,
    updates: Partial<Types.MaterialRequest>
  ): Promise<Types.MaterialRequest> {
    const response = await this.client.patch<Types.MaterialRequest>(
      `/material-requests/request/${id}`,
      updates
    );
    return response.data;
  }

  async submitMaterialRequest(id: string): Promise<Types.MaterialRequest> {
    const response = await this.client.post<Types.MaterialRequest>(
      `/material-requests/request/${id}/submit`
    );
    return response.data;
  }

  async approveMaterialRequest(id: string): Promise<Types.MaterialRequest> {
    const response = await this.client.post<Types.MaterialRequest>(
      `/material-requests/request/${id}/approve`
    );
    return response.data;
  }

  // =========================================================================
  // Shipping Lists (Phase 2)
  // =========================================================================

  async createShippingList(
    request: Types.ShippingListCreateRequest
  ): Promise<Types.ShippingList> {
    const response = await this.client.post<Types.ShippingList>(
      "/material-requests/shipping-list",
      request
    );
    return response.data;
  }

  async listShippingLists(
    params?: Partial<Types.ListQuery> & {
      material_request_id?: string;
      shipping_type?: string;
    }
  ): Promise<Types.PagedResponse<Types.ShippingList>> {
    const response = await this.client.get<
      Types.PagedResponse<Types.ShippingList>
    >("/material-requests/shipping-list", { params });
    return response.data;
  }

  async getShippingList(id: string): Promise<Types.ShippingList> {
    const response = await this.client.get<Types.ShippingList>(
      `/material-requests/shipping-list/${id}`
    );
    return response.data;
  }

  async updateShippingList(
    id: string,
    updates: Partial<Types.ShippingList>
  ): Promise<Types.ShippingList> {
    const response = await this.client.patch<Types.ShippingList>(
      `/material-requests/shipping-list/${id}`,
      updates
    );
    return response.data;
  }

  // =========================================================================
  // Toolstrings (Phase 2)
  // =========================================================================

  async createToolstring(
    request: Types.ToolstringCreateRequest
  ): Promise<Types.Toolstring> {
    const response = await this.client.post<Types.Toolstring>(
      "/material-requests/toolstring",
      request
    );
    return response.data;
  }

  async listToolstrings(
    params?: Partial<Types.ListQuery> & { operation_id?: string; is_active?: boolean }
  ): Promise<Types.PagedResponse<Types.Toolstring>> {
    const response = await this.client.get<
      Types.PagedResponse<Types.Toolstring>
    >("/material-requests/toolstring", { params });
    return response.data;
  }

  async getToolstring(id: string): Promise<Types.Toolstring> {
    const response = await this.client.get<Types.Toolstring>(
      `/material-requests/toolstring/${id}`
    );
    return response.data;
  }

  async updateToolstring(
    id: string,
    updates: Partial<Types.Toolstring>
  ): Promise<Types.Toolstring> {
    const response = await this.client.patch<Types.Toolstring>(
      `/material-requests/toolstring/${id}`,
      updates
    );
    return response.data;
  }

  async logToolstringUsage(
    toolstringId: string,
    usage: Types.ToolstringUsageLog
  ): Promise<Types.ToolstringUsageLog> {
    const response = await this.client.post<Types.ToolstringUsageLog>(
      `/material-requests/toolstring/${toolstringId}/log-usage`,
      usage
    );
    return response.data;
  }

  // =========================================================================
  // Operations & Activities (Phase 3)
  // =========================================================================

  async createDailyReport(
    request: Partial<Types.DailyReport>
  ): Promise<Types.DailyReport> {
    const response = await this.client.post<Types.DailyReport>(
      "/activities/daily-reports",
      request
    );
    return response.data;
  }

  async listDailyReports(
    params?: Partial<Types.ListQuery> & { operation_id?: string }
  ): Promise<Types.PagedResponse<Types.DailyReport>> {
    const response = await this.client.get<
      Types.PagedResponse<Types.DailyReport>
    >("/activities/daily-reports", { params });
    return response.data;
  }

  async getDailyReport(id: string): Promise<Types.DailyReport> {
    const response = await this.client.get<Types.DailyReport>(
      `/activities/daily-reports/${id}`
    );
    return response.data;
  }

  async createTimeLog(
    request: Partial<Types.ActivityTimeLog>
  ): Promise<Types.ActivityTimeLog> {
    const response = await this.client.post<Types.ActivityTimeLog>(
      "/activities/time-logs",
      request
    );
    return response.data;
  }

  async listTimeLogs(
    params?: Partial<Types.ListQuery> & {
      operation_id?: string;
      activity_type?: string;
    }
  ): Promise<Types.PagedResponse<Types.ActivityTimeLog>> {
    const response = await this.client.get<
      Types.PagedResponse<Types.ActivityTimeLog>
    >("/activities/time-logs", { params });
    return response.data;
  }

  async getActivitySummary(operationId: string): Promise<Types.ActivitySummary> {
    const response = await this.client.get<Types.ActivitySummary>(
      `/activities/operations/${operationId}/activity-summary`
    );
    return response.data;
  }

  // =========================================================================
  // RIS Reports (Phase 3)
  // =========================================================================

  async createRISReport(
    request: Types.RISReportCreateRequest
  ): Promise<Types.RISReport> {
    const response = await this.client.post<Types.RISReport>(
      "/reports/ris",
      request
    );
    return response.data;
  }

  async listRISReports(
    params?: Partial<Types.ListQuery> & {
      severity?: string;
      status?: string;
      category?: string;
    }
  ): Promise<Types.PagedResponse<Types.RISReport>> {
    const response = await this.client.get<Types.PagedResponse<Types.RISReport>>(
      "/reports/ris",
      { params }
    );
    return response.data;
  }

  async getRISReport(id: string): Promise<Types.RISReport> {
    const response = await this.client.get<Types.RISReport>(`/reports/ris/${id}`);
    return response.data;
  }

  async updateRISReport(
    id: string,
    updates: Partial<Types.RISReport>
  ): Promise<Types.RISReport> {
    const response = await this.client.patch<Types.RISReport>(
      `/reports/ris/${id}`,
      updates
    );
    return response.data;
  }

  async uploadRISPhoto(
    risId: string,
    file: File,
    description?: string
  ): Promise<Types.RISPhoto> {
    const formData = new FormData();
    formData.append("file", file);
    if (description) {
      formData.append("description", description);
    }

    const response = await this.client.post<Types.RISPhoto>(
      `/reports/ris/${risId}/photos`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  }

  async listRISPhotos(risId: string): Promise<Types.RISPhoto[]> {
    const response = await this.client.get<Types.RISPhoto[]>(
      `/reports/ris/${risId}/photos`
    );
    return response.data;
  }

  async deleteRISPhoto(risId: string, photoId: string): Promise<void> {
    await this.client.delete(`/reports/ris/${risId}/photos/${photoId}`);
  }

  async getRISStatistics(days?: number): Promise<Types.RISStatistics> {
    const response = await this.client.get<Types.RISStatistics>(
      "/reports/ris/statistics/summary",
      { params: { days } }
    );
    return response.data;
  }

  // =========================================================================
  // Job Certificates (Phase 3)
  // =========================================================================

  async createJobCertificate(
    request: Types.JobCertificateCreateRequest
  ): Promise<Types.JobCertificate> {
    const response = await this.client.post<Types.JobCertificate>(
      "/certificates/job",
      request
    );
    return response.data;
  }

  async listJobCertificates(
    params?: Partial<Types.ListQuery> & {
      operation_id?: string;
      status?: string;
    }
  ): Promise<Types.PagedResponse<Types.JobCertificate>> {
    const response = await this.client.get<
      Types.PagedResponse<Types.JobCertificate>
    >("/certificates/job", { params });
    return response.data;
  }

  async getJobCertificate(id: string): Promise<Types.JobCertificate> {
    const response = await this.client.get<Types.JobCertificate>(
      `/certificates/job/${id}`
    );
    return response.data;
  }

  async updateJobCertificate(
    id: string,
    updates: Partial<Types.JobCertificate>
  ): Promise<Types.JobCertificate> {
    const response = await this.client.patch<Types.JobCertificate>(
      `/certificates/job/${id}`,
      updates
    );
    return response.data;
  }

  async approveCertificate(
    id: string,
    action: "submit" | "approve" | "reject" | "sign",
    comments?: string,
    reviewed_by_id?: string,
    signed_by_id?: string
  ): Promise<Types.JobCertificate> {
    const response = await this.client.post<Types.JobCertificate>(
      `/certificates/job/${id}/approve`,
      {
        action,
        comments,
        reviewed_by_id,
        signed_by_id,
      }
    );
    return response.data;
  }

  async getCertificateApprovalLog(
    id: string
  ): Promise<{ total: number; items: Types.CertificateApprovalLog[] }> {
    const response = await this.client.get<{
      total: number;
      items: Types.CertificateApprovalLog[];
    }>(`/certificates/job/${id}/approval-log`);
    return response.data;
  }

  // =========================================================================
  // Analytics (Phase 3-5)
  // =========================================================================

  async getOperationKPIs(
    operationId: string
  ): Promise<Types.OperationKPI> {
    const response = await this.client.get<Types.OperationKPI>(
      `/analytics/kpis/operations/${operationId}`
    );
    return response.data;
  }

  async getTenantKPIs(): Promise<Types.TenantKPI> {
    const response = await this.client.get<Types.TenantKPI>(
      "/analytics/kpis/tenant"
    );
    return response.data;
  }

  async getPersonnelPerformance(
    params?: Partial<Types.ListQuery>
  ): Promise<Types.PagedResponse<Types.PersonnelPerformance>> {
    const response = await this.client.get<
      Types.PagedResponse<Types.PersonnelPerformance>
    >("/analytics/kpis/personnel", { params });
    return response.data;
  }

  async getToolUtilization(
    params?: Partial<Types.ListQuery>
  ): Promise<Types.PagedResponse<Types.ToolUtilization>> {
    const response = await this.client.get<
      Types.PagedResponse<Types.ToolUtilization>
    >("/analytics/kpis/tools/utilization", { params });
    return response.data;
  }
}

// Export singleton instance
export const apiClient = new APIClient();

export default apiClient;
